import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useReviewPatient } from '../../../../hooks/useReviewPatient';
import { useRecentActions } from '../../../../contexts/RecentActionsContext';
import { usePatientMetadata } from '../../../../providers/PatientMetadataProvider';
import { useToast } from '../../../../providers/ToastProvider';
import { approveRetention, refuseRetentionContinue } from '../../../../services/patient/retention';
import { PatientHeaderCard } from '../PatientApprove/PatientHeaderCard';
import { PatientActionPage } from '../shared/PatientActionPage';
import { ReviewMonitoringPane } from '../shared/ReviewMonitoringPane';
import { ReviewNotesPane } from '../shared/ReviewNotesPane';
import { RetentionFooter } from './RetentionFooter';
import { ProceedRetentionModal } from './ProceedRetentionModal';
import { RejectRetentionModal } from './RejectRetentionModal';
import { isV2PatientVersion } from '@odo/shared-ui';

const CHECKLIST_URL =
    'https://drive.google.com/file/d/1CxxMb7InaX04gUNNt5kvN2Wvsiw2kntE/view?usp=sharing';

type ModalKind = 'proceed' | 'reject' | null;

const PatientReviewRetention: React.FC = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { data: patient, isLoading } = useReviewPatient();
    const { getProfilePictureUrl } = usePatientMetadata();
    const { showToast } = useToast();
    const { markActionCompleted } = useRecentActions();

    const [modal, setModal] = useState<ModalKind>(null);
    const [submitting, setSubmitting] = useState(false);

    const patientId = patient?.id ?? null;
    const patientName = useMemo(() => patient?.name ?? '', [patient]);
    const profilePicUrl = patientId ? getProfilePictureUrl(patientId) : null;
    const dmId = patient?.dmId ?? null;
    const notesPaneProps = {
        patientId,
        triggerType: 'retention_trigger',
        fallbackNotes: patient?.retentionNotes ?? null,
        checklistUrl: CHECKLIST_URL,
        checklistLabel: 'Retention Scan Submission Checklist'
    } as const;

    const invalidate = (): void => {
        if (!patientId) return;
        queryClient.invalidateQueries({ queryKey: ['patients'] });
        queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
    };

    const handleProceed = async (retainerOption: string): Promise<void> => {
        if (!patientId) return;
        setSubmitting(true);
        try {
            await approveRetention(patientId, retainerOption);
            markActionCompleted(patientId, 'Review for retention');
            showToast('Proceeding with retention', 'success');
            invalidate();
            navigate('/home');
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to approve retention';
            showToast(message, 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleReject = async (reason: string): Promise<void> => {
        if (!patientId) return;
        setSubmitting(true);
        try {
            await refuseRetentionContinue(patientId, reason);
            markActionCompleted(patientId, 'Review for retention');
            showToast('Retention rejected', 'success');
            invalidate();
            navigate('/home');
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to reject retention';
            showToast(message, 'error');
        } finally {
            setSubmitting(false);
            setModal(null);
        }
    };

    return (
        <PatientActionPage
            patientId={patientId}
            isLoading={isLoading}
            patientName={patientName}
            breadcrumbTitle="Review for retention"
            tabIdPrefix="review-retention"
            redirectToOverview={isV2PatientVersion(patient?.version)}
            leftPane={
                dmId !== null ? (
                    <ReviewMonitoringPane
                        patientId={patientId}
                        dmId={dmId}
                        totalAligners={patient?.totalAligners ?? null}
                    />
                ) : (
                    <ReviewNotesPane {...notesPaneProps} className="h-full" />
                )
            }
            caseContent={
                <>
                    <PatientHeaderCard
                        name={patientName || null}
                        email={patient?.email ?? null}
                        phone={patient?.phone ?? null}
                        dateOfBirth={patient?.dateOfBirth ?? null}
                        profilePicUrl={profilePicUrl}
                        caseType={patient?.caseType ?? null}
                        subCaseType={patient?.subCaseType ?? null}
                        manufacturer={patient?.manufacturer ?? null}
                        manufacturerLink={patient?.manufacturerLink ?? null}
                        duration={patient?.treatmentDuration ?? null}
                        compact
                    />
                    {dmId !== null && (
                        <ReviewNotesPane {...notesPaneProps} className="flex-1 min-h-32" />
                    )}
                </>
            }
            footer={
                <RetentionFooter
                    isSubmitting={submitting}
                    onProceed={() => setModal('proceed')}
                    onReject={() => setModal('reject')}
                />
            }
        >
            <ProceedRetentionModal
                open={modal === 'proceed'}
                patientName={patientName}
                isSubmitting={submitting}
                onSubmit={(retainerOption) => void handleProceed(retainerOption)}
                onClose={() => setModal(null)}
            />
            <RejectRetentionModal
                open={modal === 'reject'}
                patientName={patientName}
                isSubmitting={submitting}
                onSubmit={(reason) => void handleReject(reason)}
                onClose={() => setModal(null)}
            />
        </PatientActionPage>
    );
};

export default PatientReviewRetention;
